import React, { useEffect } from "react";
import {
  Phone,
  Clock,
  Plus,
  ShieldCheck,
  CreditCard,
  Cpu,
  ReceiptText,
  ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PaymentStrip from "../components/PaymentStrip";
import { SITE } from "../lib/site";

export default function Pricing() {
  useEffect(() => {
    document.title = "Simple, Upfront Pricing — The Patient Tech Bloke";
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0F2E52]">
      <Header />

      <main data-testid="pricing-page">
        {/* Hero header */}
        <section className="relative overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F5FAEC] via-white to-[#F0F7E8]" />
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#6BAF3C]/15 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0F2E52]/10 blur-3xl" />
          </div>
          <div className="max-w-6xl mx-auto px-5 md:px-8 pt-6 md:pt-16 pb-5 md:pb-12 text-center">
            <span
              data-testid="pricing-badge"
              className="inline-flex items-center gap-2 bg-[#6BAF3C] text-white font-[Work_Sans] font-black text-[10px] md:text-sm uppercase tracking-widest rounded-full px-3 py-1 md:px-4 md:py-1.5 shadow-sm"
            >
              <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.8} />
              Clear · Upfront · No surprises
            </span>
            <h1
              data-testid="pricing-heading"
              className="mt-3 md:mt-4 font-[Work_Sans] font-black tracking-tight text-[#0F2E52] leading-[1.02] text-3xl sm:text-5xl lg:text-6xl"
            >
              Simple, Upfront{" "}
              <span className="text-[#6BAF3C]">Pricing</span>.
            </h1>
            <p className="mt-3 md:mt-4 text-base md:text-2xl font-semibold text-[#0F2E52]">
              Know what you’ll pay before you book.
            </p>
            <p className="mt-2 md:mt-3 text-sm md:text-lg text-[#334155] max-w-2xl mx-auto">
              Our standard on-site service pricing is simple and clearly explained
              before you make an appointment.
            </p>
            <div className="mt-5 md:mt-7 max-w-2xl mx-auto text-left">
              <PaymentStrip />
            </div>
          </div>
        </section>

        {/* Price cards */}
        <section className="py-4 md:py-8" data-testid="price-cards">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="grid md:grid-cols-2 gap-4 md:gap-8">
              {/* First hour */}
              <article
                data-testid="price-card-first-hour"
                className="relative rounded-2xl md:rounded-3xl bg-[#0F2E52] text-white p-5 md:p-10 shadow-xl md:shadow-2xl shadow-[#0F2E52]/20 overflow-hidden"
              >
                <div
                  aria-hidden="true"
                  className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#6BAF3C]/25 blur-3xl"
                />
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-2.5 py-1 text-[10px] md:text-xs font-semibold uppercase tracking-widest">
                    <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
                    Standard rate
                  </div>
                  <div className="mt-3 md:mt-5 flex items-baseline gap-2 flex-wrap">
                    <span className="font-[Work_Sans] font-black text-5xl md:text-7xl leading-none">
                      $139
                    </span>
                    <span className="text-base md:text-xl font-semibold text-white/90">
                      first hour
                    </span>
                  </div>
                  <p className="mt-3 md:mt-4 text-base md:text-lg text-white/95 leading-snug">
                    Includes up to the first{" "}
                    <strong className="text-white">60 minutes</strong> of service.
                  </p>
                  <p className="mt-2 md:mt-3 text-xs md:text-sm text-white/80">
                    A flat, honest rate — no travel fee, no callout fee.
                  </p>
                </div>
              </article>

              {/* Each additional 30 min */}
              <article
                data-testid="price-card-additional"
                className="relative rounded-2xl md:rounded-3xl bg-white border-2 border-[#0F2E52] p-5 md:p-10 shadow-md md:shadow-xl"
              >
                <div className="inline-flex items-center gap-1.5 bg-[#F0F7E8] text-[#0F2E52] border border-[#DCE4EC] rounded-full px-2.5 py-1 text-[10px] md:text-xs font-semibold uppercase tracking-widest">
                  <Plus className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
                  If more time is needed
                </div>
                <div className="mt-3 md:mt-5 flex items-baseline gap-2 flex-wrap">
                  <span className="font-[Work_Sans] font-black text-5xl md:text-7xl leading-none text-[#0F2E52]">
                    $60
                  </span>
                  <span className="text-base md:text-xl font-semibold text-[#334155]">
                    each additional 30 min
                  </span>
                </div>
                <p className="mt-3 md:mt-4 text-base md:text-lg text-[#334155] leading-snug">
                  After the first hour, additional time is charged at{" "}
                  <strong className="text-[#0F2E52]">$60 per extra 30 minutes</strong>.
                </p>
                <p className="mt-2 md:mt-3 text-xs md:text-sm text-[#475569]">
                  If more time is required, the technician will discuss this with you{" "}
                  <strong>before continuing</strong>.
                </p>
              </article>
            </div>

            {/* No hidden rates strip */}
            <div
              data-testid="no-hidden-rates"
              className="mt-8 md:mt-10 rounded-2xl bg-[#F0F7E8] border border-[#C7DDA6] px-5 md:px-8 py-5 md:py-6 flex items-start gap-4"
            >
              <ShieldCheck
                className="w-6 h-6 md:w-7 md:h-7 text-[#6BAF3C] shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
              <p className="text-base md:text-lg text-[#0F2E52] leading-snug">
                <strong>No hidden hourly rates.</strong> You don’t need to contact
                us just to find out our standard service pricing — it's right here
                on this page.
              </p>
            </div>
          </div>
        </section>

        {/* Hardware & Software */}
        <section className="py-8 md:py-14" data-testid="hardware-section">
          <div className="max-w-4xl mx-auto px-5 md:px-8">
            <div className="rounded-2xl md:rounded-3xl bg-white border border-[#DCE4EC] p-5 md:p-10 shadow-sm">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#F0F7E8] grid place-items-center shrink-0">
                  <Cpu className="w-5 h-5 md:w-7 md:h-7 text-[#6BAF3C]" strokeWidth={2.3} />
                </div>
                <div>
                  <h2 className="font-[Work_Sans] font-black text-xl md:text-3xl text-[#0F2E52] leading-tight">
                    Hardware &amp; Software
                  </h2>
                  <p className="mt-2 md:mt-3 text-sm md:text-lg text-[#334155]">
                    The prices above cover the technician’s{" "}
                    <strong className="text-[#0F2E52]">service and labour</strong>.
                  </p>
                  <p className="mt-2 md:mt-3 text-sm md:text-lg text-[#334155]">
                    If any hardware, parts, software or licences are required to
                    complete the work, the technician will{" "}
                    <strong className="text-[#0F2E52]">
                      discuss this with you during the visit
                    </strong>
                    , including any additional costs, before anything is purchased
                    or supplied.
                  </p>
                  <p className="mt-3 md:mt-4 text-sm md:text-lg font-semibold text-[#0F2E52]">
                    No additional hardware or software costs will be added without
                    discussing them with you first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment on Completion */}
        <section className="pb-8 md:pb-14" data-testid="payment-section">
          <div className="max-w-4xl mx-auto px-5 md:px-8">
            <div className="rounded-2xl md:rounded-3xl bg-[#0F2E52] text-white p-5 md:p-10 shadow-lg md:shadow-xl">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/10 border border-white/20 grid place-items-center shrink-0">
                  <CreditCard className="w-5 h-5 md:w-7 md:h-7 text-[#6BAF3C]" strokeWidth={2.3} />
                </div>
                <div>
                  <h2 className="font-[Work_Sans] font-black text-xl md:text-3xl leading-tight">
                    Payment on Completion
                  </h2>
                  <p className="mt-2 md:mt-3 text-base md:text-xl font-semibold text-[#6BAF3C]">
                    EFTPOS or Cash — paid on the day the job is finished.
                  </p>
                  <ul className="mt-3 md:mt-4 space-y-2 md:space-y-3 text-sm md:text-lg text-white/95">
                    <li className="flex items-start gap-2 md:gap-3">
                      <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#6BAF3C] shrink-0 mt-0.5 md:mt-1" strokeWidth={2.6} />
                      <span>
                        Payment is due by <strong className="text-white">EFTPOS or cash</strong> on completion of the work.
                      </span>
                    </li>
                    <li className="flex items-start gap-2 md:gap-3">
                      <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#6BAF3C] shrink-0 mt-0.5 md:mt-1" strokeWidth={2.6} />
                      <span>
                        EFTPOS is contactless — tap your card or phone to pay.
                      </span>
                    </li>
                    <li className="flex items-start gap-2 md:gap-3">
                      <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#6BAF3C] shrink-0 mt-0.5 md:mt-1" strokeWidth={2.6} />
                      <span>
                        No online payments, no cards on file, no surprise charges.
                      </span>
                    </li>
                    <li className="flex items-start gap-2 md:gap-3">
                      <ReceiptText className="w-4 h-4 md:w-5 md:h-5 text-[#6BAF3C] shrink-0 mt-0.5 md:mt-1" strokeWidth={2.6} />
                      <span>A receipt will be provided following payment.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-16 md:pb-24" data-testid="pricing-cta">
          <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
            <h2 className="font-[Work_Sans] font-black text-2xl md:text-4xl text-[#0F2E52] leading-tight">
              Ready to book a visit?
            </h2>
            <p className="mt-3 text-base md:text-lg text-[#334155]">
              Give Tom a ring — you’ll get a friendly local, not a call centre.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-stretch">
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid="pricing-cta-call"
                className="inline-flex items-center justify-center gap-3 bg-[#0F2E52] hover:bg-[#0A2340] text-white font-bold rounded-xl px-6 py-4 text-lg md:text-xl shadow-lg min-h-[60px]"
              >
                <Phone className="w-6 h-6" strokeWidth={2.5} />
                Call {SITE.phone}
              </a>
              <a
                href="/#book"
                data-testid="pricing-cta-book"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0F2E52] text-[#0F2E52] hover:bg-[#F0F7E8] font-bold rounded-xl px-6 py-4 text-lg md:text-xl min-h-[60px]"
              >
                Book / Contact Us
              </a>
            </div>
            <p className="mt-5 text-sm md:text-base text-[#475569]">
              Free 5-minute chat first · Adelaide local · No overseas call centres
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
